import type { ContextTreeAccount, ContextTreeMc } from '@/hooks/useContextTree';
import { normalizeForSearch } from './switcherSearch';

/**
 * Anzeigename einer MC. `ContextTreeMc.name` ist `string | null`
 * (useContextTree.ts:22); ohne Namen steht die GMC-ID da, nie eine leere Zeile.
 */
export function mcDisplayName(mc: ContextTreeMc): string {
  return (mc.name ?? '').trim() || mc.externalId;
}

/** Normalisierter Name fuer Vergleiche, `''` ohne Namen. */
export function mcNameKey(mc: ContextTreeMc): string {
  return normalizeForSearch((mc.name ?? '').trim());
}

/**
 * A3-12: ein Agenturkonto fuehrt 83 Merchant Center, viele davon ohne Feed
 * oder mit entzogenem Zugriff. Die stehen jetzt in einer eingeklappten
 * Gruppe am Ende des Kontos. Die GEWAEHLTE MC bleibt immer oben, sonst
 * verschwaende der aktive Feed genau dann, wenn sein Zugriff entzogen ist.
 * MCs mit nur ausgeblendeten Google-Feeds bleiben oben, sie tragen den
 * Hinweis "automatisch erstellte Feeds ausgeblendet".
 */
export function partitionMerchantCenters(
  mcs: ContextTreeMc[],
  selectedMcId: string | null,
): { primary: ContextTreeMc[]; inactive: ContextTreeMc[] } {
  const primary: ContextTreeMc[] = [];
  const inactive: ContextTreeMc[] = [];
  for (const mc of mcs) {
    const isInactive =
      mc.id !== selectedMcId &&
      (mc.accessStatus !== 'active' || (mc.feeds.length === 0 && mc.hiddenAutomaticCount === 0));
    (isInactive ? inactive : primary).push(mc);
  }
  return { primary, inactive };
}

/**
 * Namen, die im selben Konto mehrfach vorkommen. Live am 26.09.2026:
 * bernheim.ch, Laderach.com, Spielgigant.ch je zweimal, jeweils zwei
 * verschiedene GMC-IDs. Solche Zeilen zeigen zusaetzlich ihre GMC-ID.
 * MCs ohne Namen zaehlen nicht, sie zeigen ohnehin ihre GMC-ID.
 */
export function duplicateMcNames(mcs: ContextTreeMc[]): Set<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const mc of mcs) {
    const key = mcNameKey(mc);
    if (!key) continue;
    if (seen.has(key)) dupes.add(key);
    seen.add(key);
  }
  return dupes;
}

/**
 * A3-17: Namen, die in MEHR ALS EINEM Konto vorkommen. Nur fuer sie braucht
 * der Suchpfad den Kontoteil; sonst belegte "Google | OMA AG ›" mobil die
 * halbe Zeile und kuerzte den Feednamen.
 */
export function namesInSeveralAccounts(accounts: ContextTreeAccount[]): Set<string> {
  const owner = new Map<string, string>();
  const several = new Set<string>();
  for (const acc of accounts) {
    for (const mc of acc.merchantCenters) {
      const key = mcNameKey(mc);
      if (!key) continue;
      const first = owner.get(key);
      if (first === undefined) owner.set(key, acc.id);
      else if (first !== acc.id) several.add(key);
    }
  }
  return several;
}
